import filter from '../../filter';
import Listing from '../Listing/Listing';
import {executeQuery} from '../../requests/executeQuery';
import {executeBackendQuery} from '../../requests/executeBackendQuery';
import {getData} from '../../requests/CRUD';
import {ownerResearchApi} from '../../requests/ownerResearch';
import {TEAM, TERRITORIES_V2, MARKETS} from '../../consts/tables';
import {
	V7_PROPERTIES_LIST,
	V7_PROPERTIES_STATS,
	V7_PROPERTIES_STATS_ACTIVE,
	V7_PROPERTIES_STATS_COUNT,
	V9_PROPERTIES_LIST,
	V9_PROPERTIES_STATS,
	V9_PROPERTIES_STATS_COUNT,
	V10_PROPERTIES_LIST,
	V10_PROPERTIES_STATS,
	V10_PROPERTIES_STATS_COUNT,
	V7_TERRITORY_FLAG_STATS,
	V9_TERRITORY_FLAG_STATS,
	V10_TERRITORY_FLAG_STATS,
	V9_HOT_LIST_COUNT,
	V10_HOT_LIST_COUNT,
	GET_ALL_PRODUCT_TYPES,
	INVESTOR_HOT_LIST_COUNT,
} from '../../consts/queries';
import {normalizeListQuery, preprocessQueryFilters} from '../../utils';
import {can} from '../../access';

const cloneDeep = require('lodash.clonedeep');

// The groups table's own sort. It has its own URL key because `order` belongs
// to the property filters, which read it.
export const GROUP_ORDER_KEY = 'group_order';

/**
 * The URL query with the groups page's own keys taken out, leaving only what
 * the properties list would have been given.
 */
export function listQueryOf(locationQuery) {
	const query = {...locationQuery};
	delete query[GROUP_ORDER_KEY];
	return query;
}

/**
 * Which queries the list's filters resolve to, and the options each is run
 * with. Mirrors the first half of Listing.fetch() and must be kept in step
 * with it: the figures on this page are the ones /list shows for the same URL.
 */
export function buildListQueries(locationQuery, userData) {
	const query = preprocessQueryFilters(normalizeListQuery(listQueryOf(locationQuery)), userData);

	let page = 'properties';
	let statsQueryName =
		query.hasOwnProperty('SOURCE') && query.SOURCE === 'SUMMIT' &&
		query.hasOwnProperty('STATUS') && query.STATUS === 'Active'
			? V7_PROPERTIES_STATS_ACTIVE
			: V7_PROPERTIES_STATS;
	let propertiesQueryName = V7_PROPERTIES_LIST;
	let countQueryName = V7_PROPERTIES_STATS_COUNT;
	const isTeamFilter = query.hasOwnProperty('TERRITORY_LISTING_AGENT_TEAM') &&
		!['Territory1', 'Territory2', 'Territory3', 'Territory4'].includes(query.TERRITORY_LISTING_AGENT_TEAM);
	const hasExtendedTerritoryFilter =
		query.hasOwnProperty('PRODUCT_TYPE') ||
		query.hasOwnProperty('TERRITORY') ||
		query.hasOwnProperty('TERRITORY_MARKET');

	if (isTeamFilter && !hasExtendedTerritoryFilter) {
		page = 'territories';
		statsQueryName = V9_PROPERTIES_STATS;
		countQueryName = V9_PROPERTIES_STATS_COUNT;
		propertiesQueryName = V9_PROPERTIES_LIST;
	} else if (isTeamFilter || hasExtendedTerritoryFilter) {
		page = 'territories';
		statsQueryName = V10_PROPERTIES_STATS;
		countQueryName = V10_PROPERTIES_STATS_COUNT;
		propertiesQueryName = V10_PROPERTIES_LIST;
	}

	const options = Listing.getOptions(query, page);
	// Stats/count queries return a single aggregate row, so the offset stays 0.
	const aggregateOptions = cloneDeep(options);
	aggregateOptions.param.offset = 0;
	aggregateOptions.param.limit = 1;

	let flagStatsQueryName = V7_TERRITORY_FLAG_STATS;
	let hotListCountQueryName = V9_HOT_LIST_COUNT;
	if (propertiesQueryName === V10_PROPERTIES_LIST) {
		flagStatsQueryName = V10_TERRITORY_FLAG_STATS;
		hotListCountQueryName = V10_HOT_LIST_COUNT;
	} else if (propertiesQueryName === V9_PROPERTIES_LIST) {
		flagStatsQueryName = V9_TERRITORY_FLAG_STATS;
	}

	// "Closed - Last 12 Mo": the same filters with Closed and a rolling 12 month start.
	const twelveMonthsAgo = new Date();
	twelveMonthsAgo.setFullYear(twelveMonthsAgo.getFullYear() - 1);
	const closed12mDate = `${(twelveMonthsAgo.getMonth() + 1).toString().padStart(2, '0')}/${twelveMonthsAgo.getDate().toString().padStart(2, '0')}/${twelveMonthsAgo.getFullYear()}`;
	const closed12mQuery = {...query, STATUS: 'Closed', CLOSE_OF_ESCROW_MIN: closed12mDate};
	delete closed12mQuery.order;
	delete closed12mQuery.page;
	const closed12mOptions = filter(closed12mQuery, page);
	closed12mOptions.param.limit = 1;
	closed12mOptions.param.offset = 0;

	return {
		query,
		page,
		propertiesQueryName,
		statsQueryName,
		countQueryName,
		flagStatsQueryName,
		hotListCountQueryName,
		options,
		aggregateOptions,
		closed12mOptions,
		signature: Listing.aggregateSignature(query, page, statsQueryName, countQueryName, flagStatsQueryName, hotListCountQueryName),
	};
}

const none = () => ({success: false, data: []});

/**
 * Runs the five aggregate queries /list runs for its stats header, staggered
 * the same way. Resolves with their raw results, in the same order.
 */
export function fetchStatsResults(plan, userData) {
	const delayExec = (fn, ms) => new Promise((resolve) => setTimeout(() => resolve(fn()), ms));
	const run = (queryName, options) => executeQuery({queryName, options: cloneDeep(options), expireTime: 1000});

	return Promise.all([
		delayExec(() => run(plan.statsQueryName, plan.aggregateOptions), 15),
		delayExec(() => run(plan.countQueryName, plan.aggregateOptions), 30),
		delayExec(() => run(plan.flagStatsQueryName, plan.aggregateOptions).catch(none), 45),
		delayExec(() => run(plan.statsQueryName, plan.closed12mOptions).catch(none), 60),
		delayExec(() => {
			const userEmail = userData && userData.email;
			// Hot List is per agent, not per territory, so the figure ignores the filters.
			return executeQuery({
				queryName: plan.hotListCountQueryName,
				options: {param: {HOT_LIST_USER_EMAIL: userEmail || ''}},
				expireTime: 1000,
			}).catch(none);
		}, 75),
	]);
}

const firstRow = (result) =>
	result && result.success && Array.isArray(result.data) && result.data.length ? result.data[0] : null;

/**
 * The stats object and territory flag counts ListStats is given, worked out
 * of fetchStatsResults() the way Listing.fetch() does it.
 */
export function statsFromResults(values) {
	const stats = firstRow(values[0]);

	if (stats) {
		const closed12mRow = firstRow(values[3]);
		if (closed12mRow) {
			stats.CLOSED_LAST_12M = closed12mRow.count;
			stats.CLOSED_CAP_LAST_12M = closed12mRow.CLOSE_CAP_RATE;
		}

		// Averages that leave out the zeros.
		['LIST_PRICE', 'CAPITALIZATION_RATE', 'CLOSE_CAP_RATE', 'NET_OPERATING_INCOME', 'PRICE_PER_SQFT', 'CLOSE_PRICE_PER_SQFT', 'RENT_PER_SQFT', 'SOLD_PRICE', 'VACANCY'].forEach((f) => {
			const sum = Number(stats[f + '_NZ_SUM']) || 0;
			const count = Number(stats[f + '_NZ_CNT']) || 0;
			if (count > 0) stats[f] = sum / count;
		});
	}

	const flagStatsRow = firstRow(values[2]);
	const hotListRow = firstRow(values[4]);
	const territoryFlagCounts = flagStatsRow
		? {
			hotList: hotListRow ? Number(hotListRow.HOT_LIST_COUNT) || 0 : 0,
			newDev: Number(flagStatsRow.NEW_DEV_COUNT) || 0,
			needOwner: (Number(flagStatsRow.TOTAL_PROPERTY_COUNT) || 0)
				- (Number(flagStatsRow.HAS_OWNERSHIP_COUNT) || 0)
				+ (Number(flagStatsRow.NEED_OWNER_WITH_OWNERSHIP_COUNT) || 0),
			researched: Number(flagStatsRow.RESEARCHED_COUNT) || 0,
			needInfo: Number(flagStatsRow.NEED_INFO_COUNT) || 0,
		}
		: null;

	return {stats, territoryFlagCounts};
}

export function fetchInvestorHotListCount(userData) {
	const userEmail = userData && userData.email;
	if (!userEmail) return Promise.resolve(0);
	return executeQuery({
		queryName: INVESTOR_HOT_LIST_COUNT,
		options: {param: {USER_EMAIL: userEmail}},
		expireTime: 0,
	}).then((res) => (res && res.success && Array.isArray(res.data) && res.data[0] ? Number(res.data[0].TOTAL_COUNT) || 0 : 0))
		.catch(() => 0);
}

/** The owner research tile ListStats shows for a territory filter; null when there is none. */
export function fetchOwnerResearchReviews(locationQuery) {
	const filterOf = {};
	const idList = (value) => (value && /^\d+(,\d+)*$/.test(String(value)) ? String(value) : null);

	if (idList(locationQuery.TERRITORY_LISTING_AGENT_TEAM)) filterOf.agent = idList(locationQuery.TERRITORY_LISTING_AGENT_TEAM);
	if (idList(locationQuery.TERRITORY)) filterOf.territory = idList(locationQuery.TERRITORY);
	if (idList(locationQuery.TERRITORY_MARKET)) filterOf.territory_market = idList(locationQuery.TERRITORY_MARKET);

	if (!Object.keys(filterOf).length || !can('true_owners')) return Promise.resolve(null);

	const link = '/owner-research?' + Object.keys(filterOf).map((key) => key + '=' + encodeURIComponent(filterOf[key])).join('&');
	return ownerResearchApi.reviews(1, filterOf)
		.then((envelope) => ({count: envelope.meta.pagination.total, link}))
		.catch(() => null);
}

/** The names the stats tiles show for an agent, territory, product type or market filter. */
export function fetchStatsNames(locationQuery) {
	const names = {agentName: null, territoryName: null, productTypeName: null, marketName: null};
	if (!locationQuery.TERRITORY_LISTING_AGENT_TEAM) return Promise.resolve(names);

	const idsOf = (value) => String(value).split(',').filter(Boolean);
	const namesOf = (tableCode, value) => {
		const ids = idsOf(value);
		return getData({
			tableCode,
			options: {
				limit: {count: ids.length, offset: 0},
				column: ['ARCIMEDES_ROW_ID', 'NAME'],
				filter: ids.map((id) => ({code: 'ARCIMEDES_ROW_ID', text: id, type: 'equal'})),
				filterType: 'or',
			},
		}).then((res) => (res && res.success && res.data && res.data.length ? res.data.map((row) => row.NAME).filter(Boolean).join(', ') || null : null))
			.catch(() => null);
	};

	const agent = getData({
		tableCode: TEAM,
		options: {
			limit: {count: 1, offset: 0},
			column: ['ARCIMEDES_ROW_ID', 'FIRST_NAME', 'LAST_NAME'],
			filter: [{code: 'ARCIMEDES_ROW_ID', text: String(locationQuery.TERRITORY_LISTING_AGENT_TEAM), type: 'equal'}],
			filterType: 'and',
		},
	}).then((res) => (res && res.success && res.data && res.data.length
		? [res.data[0].FIRST_NAME, res.data[0].LAST_NAME].filter(Boolean).join(' ') || null
		: null)).catch(() => null);

	const productTypes = locationQuery.PRODUCT_TYPE
		? executeBackendQuery({queryName: GET_ALL_PRODUCT_TYPES, options: {param: {}}}).then((res) => {
			const ids = idsOf(locationQuery.PRODUCT_TYPE);
			return res && res.success && res.data && res.data.length
				? res.data.filter((pt) => ids.includes(String(pt.ARCIMEDES_ROW_ID))).map((pt) => pt.NAME).filter(Boolean).join(', ') || null
				: null;
		}).catch(() => null)
		: Promise.resolve(null);

	return Promise.all([
		agent,
		locationQuery.TERRITORY ? namesOf(TERRITORIES_V2, locationQuery.TERRITORY) : Promise.resolve(null),
		productTypes,
		locationQuery.TERRITORY_MARKET ? namesOf(MARKETS, locationQuery.TERRITORY_MARKET) : Promise.resolve(null),
	]).then(([agentName, territoryName, productTypeName, marketName]) => ({agentName, territoryName, productTypeName, marketName}));
}
